import { defaultSchema, type Options as Schema } from "rehype-sanitize";

/**
 * Sanitizer schema for release-note content.
 *
 * Release notes are authored by our own release-bot (not untrusted users), but
 * they go through the same Markdown → HTML pipeline as anything that might be
 * extended to user-facing content later, so we keep a strict allowlist and
 * explicitly opt into the SVG / styling tags the rich-visuals skill needs.
 *
 * What's allowed on top of GitHub-flavored defaults:
 *   - Inline `style` attribute on all elements (for palettes / stat cards)
 *   - Full SVG element and attribute set (charts, diagrams, icons)
 *   - `class` on `<span>` and `<code>` so code-highlight markers (kw, str, …)
 *     survive the pipeline
 *   - `<details>` / `<summary>` for collapsible sections
 *
 * What is explicitly NOT allowed:
 *   - `<script>`, event handlers (`onclick`, …) — stripped by default
 *   - External resources — `<link>`, `<iframe>`, `<object>`, `<embed>`
 *   - Form elements — `<form>`, `<input>`, `<button>` (beyond styling)
 */

// Attributes common to every SVG element — geometry, styling, accessibility.
const SVG_COMMON_ATTRS: (string | [string, ...(string | number | boolean)[]])[] = [
  "id",
  "class",
  "style",
  "fill",
  "stroke",
  "strokeWidth",
  "stroke-width",
  "strokeLinecap",
  "stroke-linecap",
  "strokeLinejoin",
  "stroke-linejoin",
  "strokeDasharray",
  "stroke-dasharray",
  "strokeDashoffset",
  "stroke-dashoffset",
  "strokeMiterlimit",
  "stroke-miterlimit",
  "fillOpacity",
  "fill-opacity",
  "fillRule",
  "fill-rule",
  "opacity",
  "transform",
  "clipPath",
  "clip-path",
  "mask",
  "filter",
  "vectorEffect",
  "vector-effect",
  "pointerEvents",
  "pointer-events",
  "cursor",
  "visibility",
  "display",
  "role",
  "aria-label",
  "aria-labelledby",
  "aria-describedby",
  "aria-hidden",
  "title",
  "tabIndex",
];

const SVG_SHAPE_ATTRS = [
  ...SVG_COMMON_ATTRS,
  "x",
  "y",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "x1",
  "y1",
  "x2",
  "y2",
  "width",
  "height",
  "points",
  "d",
  "pathLength",
  "path-length",
  "dx",
  "dy",
  "textAnchor",
  "text-anchor",
  "dominantBaseline",
  "dominant-baseline",
  "alignmentBaseline",
  "alignment-baseline",
  "fontFamily",
  "font-family",
  "fontSize",
  "font-size",
  "fontWeight",
  "font-weight",
  "fontStyle",
  "font-style",
  "textDecoration",
  "text-decoration",
  "letterSpacing",
  "letter-spacing",
  "wordSpacing",
  "word-spacing",
  "writingMode",
  "writing-mode",
  "lengthAdjust",
  "length-adjust",
  "textLength",
  "text-length",
];

export const releaseNotesSchema: Schema = {
  ...defaultSchema,

  attributes: {
    ...defaultSchema.attributes,

    // Allow style + class on every element so rich-visuals patterns work.
    "*": [
      ...((defaultSchema.attributes ?? {})["*"] ?? []),
      "style",
      "className",
      "class",
    ],

    // SVG root + viewport attrs.
    svg: [
      ...SVG_COMMON_ATTRS,
      "width",
      "height",
      "viewBox",
      "preserveAspectRatio",
      "xmlns",
      "xmlnsXlink",
      "xmlns:xlink",
      "version",
      "baseProfile",
    ],

    // SVG shapes & text.
    g: SVG_COMMON_ATTRS,
    defs: SVG_COMMON_ATTRS,
    rect: SVG_SHAPE_ATTRS,
    circle: SVG_SHAPE_ATTRS,
    ellipse: SVG_SHAPE_ATTRS,
    line: SVG_SHAPE_ATTRS,
    polyline: SVG_SHAPE_ATTRS,
    polygon: SVG_SHAPE_ATTRS,
    path: SVG_SHAPE_ATTRS,
    text: SVG_SHAPE_ATTRS,
    tspan: SVG_SHAPE_ATTRS,
    textPath: [...SVG_SHAPE_ATTRS, "href", "xlinkHref", "startOffset"],

    // Gradients, patterns, markers.
    linearGradient: [...SVG_COMMON_ATTRS, "x1", "y1", "x2", "y2", "gradientUnits", "gradientTransform", "spreadMethod"],
    radialGradient: [...SVG_COMMON_ATTRS, "cx", "cy", "r", "fx", "fy", "fr", "gradientUnits", "gradientTransform", "spreadMethod"],
    stop: [...SVG_COMMON_ATTRS, "offset", "stopColor", "stop-color", "stopOpacity", "stop-opacity"],
    pattern: [...SVG_COMMON_ATTRS, "x", "y", "width", "height", "patternUnits", "patternContentUnits", "patternTransform", "viewBox", "preserveAspectRatio", "href"],
    marker: [...SVG_COMMON_ATTRS, "markerUnits", "markerWidth", "markerHeight", "orient", "refX", "refY", "viewBox", "preserveAspectRatio"],
    clipPath: [...SVG_COMMON_ATTRS, "clipPathUnits"],
    mask: [...SVG_COMMON_ATTRS, "x", "y", "width", "height", "maskUnits", "maskContentUnits"],
    filter: [...SVG_COMMON_ATTRS, "x", "y", "width", "height", "filterUnits", "primitiveUnits"],
    use: [...SVG_COMMON_ATTRS, "x", "y", "width", "height", "href", "xlinkHref"],
    symbol: [...SVG_COMMON_ATTRS, "viewBox", "preserveAspectRatio"],
    foreignObject: [...SVG_COMMON_ATTRS, "x", "y", "width", "height"],
    title: SVG_COMMON_ATTRS,
    desc: SVG_COMMON_ATTRS,

    // Expanded code-highlight support (rich-visuals uses `<span class="kw">` etc.).
    span: [...((defaultSchema.attributes ?? {}).span ?? []), "class", "className", "style", "title"],
    code: [...((defaultSchema.attributes ?? {}).code ?? []), "class", "className", "style"],

    // Collapsible sections.
    details: ["open"],
    summary: [],
  },

  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    // SVG
    "svg", "g", "defs", "rect", "circle", "ellipse", "line", "polyline",
    "polygon", "path", "text", "tspan", "textPath", "linearGradient",
    "radialGradient", "stop", "pattern", "marker", "clipPath", "mask",
    "filter", "use", "symbol", "foreignObject", "desc",
    // Collapsible
    "details", "summary",
  ],

  // Deduplicate after extension (an earlier spread may have already included a tag).
};
